import { useEffect, useRef, useState } from "react";

import { InnerPageChrome } from "@/components/layout/InnerPageChrome";
import { SITE_ROUTES } from "@/config/siteRoutes";
import { usePortfolioArchive } from "@/data/portfolioArchive";
import { useVorinAssets } from "@/pages/shared/useVorinAssets";
import { MINIMALISTIC_PORTFOLIO_STYLESHEETS } from "@/pages/shared/vorinDemoStyles";

import styles from "./PortfolioPage.module.scss";

export function PortfolioPage() {
  useVorinAssets({ extraStylesheets: MINIMALISTIC_PORTFOLIO_STYLESHEETS });
  const data = usePortfolioArchive();
  const content = data.presentation;
  const featureVisualUrl = content.feature_visual.image || content.feature_visual.fallback_url || "";
  const visualSectionRef = useRef<HTMLElement | null>(null);
  const [visualProgress, setVisualProgress] = useState(0);
  const [viewportSize, setViewportSize] = useState(() => ({
    width: typeof window === "undefined" ? 1440 : window.innerWidth,
    height: typeof window === "undefined" ? 900 : window.innerHeight,
  }));

  useEffect(() => {
    document.title = `${data.seoTitle || data.title} | VenueX`;
    document
      .querySelector('meta[name="description"]')
      ?.setAttribute("content", data.searchDescription || data.intro);
  }, [data.intro, data.searchDescription, data.seoTitle, data.title]);

  useEffect(() => {
    const update = () => {
      const section = visualSectionRef.current;

      setViewportSize({
        width: window.innerWidth,
        height: window.innerHeight,
      });

      if (!section) {
        return;
      }

      const rect = section.getBoundingClientRect();
      const start = window.innerHeight * 0.88;
      const end = -window.innerHeight * 0.52;
      const raw = (start - rect.top) / Math.max(start - end, 1);
      const clamped = Math.min(Math.max(raw, 0), 1);

      setVisualProgress(clamped);
    };

    update();
    window.addEventListener("scroll", update, { passive: true });
    window.addEventListener("resize", update);

    return () => {
      window.removeEventListener("scroll", update);
      window.removeEventListener("resize", update);
    };
  }, []);

  const visualEased = 1 - Math.pow(1 - visualProgress, 1.65);
  const frameWidth = Math.round(
    Math.min(viewportSize.width * 0.9, 1620) +
      (viewportSize.width - Math.min(viewportSize.width * 0.9, 1620)) * visualEased,
  );
  const frameHeight = Math.round(
    Math.min(viewportSize.height * 0.68, 760) +
      (viewportSize.height - Math.min(viewportSize.height * 0.68, 760)) * visualEased,
  );
  const frameRadius = 34 * (1 - visualEased);
  const frameShadow = 0.18 + 0.22 * visualEased;
  const imageScale = 1.16 - visualEased * 0.12;
  const imageTranslateY = 42 - visualEased * 84;
  const imageFilterBrightness = 0.82 + visualEased * 0.16;

  return (
    <InnerPageChrome currentPath={SITE_ROUTES.portfolio}>
      <article className={styles.page}>
        <section className={styles.hero}>
          <div className="container-fluid">
            <h1 className={styles.heroTitle}>{content.hero_title}</h1>
          </div>
        </section>

        <section className={styles.introSection}>
          <div className="container-fluid">
            <div className={styles.introRow}>
              <div className={styles.introBadge}>
                <span className={styles.star}>&#10038;</span>
                <div>
                  <span className={styles.kicker}>{content.intro_kicker}</span>
                  <p className={styles.kickerText}>{content.intro_copy}</p>
                </div>
              </div>

              <h2 className={styles.introHeading}>{content.intro_heading}</h2>
            </div>
          </div>
        </section>

        <section ref={visualSectionRef} className={styles.visualSection}>
          <div className={styles.visualSticky}>
            <div
              className={styles.visualFrame}
              style={
                {
                  width: `${frameWidth}px`,
                  height: `${frameHeight}px`,
                  borderRadius: `${frameRadius}px`,
                  boxShadow: `0 34px 110px rgba(18, 13, 10, ${frameShadow.toFixed(3)})`,
                }
              }
            >
              <img
                src={featureVisualUrl}
                alt={content.feature_visual.alt || ""}
                style={
                  {
                    transform: `translate3d(0, ${imageTranslateY}px, 0) scale(${imageScale.toFixed(3)})`,
                    filter: `brightness(${imageFilterBrightness.toFixed(3)})`,
                    objectPosition: content.feature_visual.position || "center center",
                  }
                }
              />
            </div>
          </div>
        </section>

        <section className={styles.marqueeSection}>
          <div className="container-fluid">
            <span className={styles.marquee}>{content.marquee_text}</span>
          </div>
        </section>

        <section className={styles.showcaseSection}>
          <div className="container-fluid">
            <div className={styles.showcaseGrid}>
              {data.items.map((card, index) => (
                <article
                  key={card.id}
                  className={`${styles.showcaseItem} ${index % 3 === 0 ? styles.motionRise : ""} ${index % 3 === 1 ? styles.motionFall : ""} ${index % 3 === 2 ? styles.motionDrift : ""} ${index % 3 === 1 ? styles.cardOffsetDown : ""} ${index % 3 === 2 ? styles.cardOffsetUp : ""}`}
                >
                  <a
                    className={`${styles.showcaseCard} ${card.cardTone === "dark" ? styles.cardDark : styles.cardLight}`}
                    href={card.url}
                  >
                    <div className={styles.cardImage}>
                      <img
                        src={card.imageUrl}
                        alt={card.imageAlt}
                        style={{ objectPosition: card.imagePosition }}
                      />
                    </div>

                    <div className={styles.cardBody}>
                      <span className={styles.cardMeta}>{card.projectType}</span>
                      <h3 className={styles.cardTitle}>{card.title}</h3>
                      <p className={styles.cardNote}>{card.summary}</p>
                    </div>
                  </a>
                </article>
              ))}
            </div>
          </div>
        </section>

        <section className={styles.signalSection}>
          <div className="container-fluid">
            <div className={styles.signalGrid}>
              {content.signals.map((item) => (
                <article key={item.label} className={styles.signalCard}>
                  <span className={styles.signalLabel}>{item.label}</span>
                  <p className={styles.signalValue}>{item.value}</p>
                </article>
              ))}
            </div>
          </div>
        </section>

        <section className={styles.ctaSection}>
          <div className="container-fluid">
            <div className={styles.ctaGrid}>
              <div>
                <span className={styles.kicker}>{content.cta_kicker}</span>
                <h2 className={styles.ctaTitle}>{content.cta_title}</h2>
              </div>

              <div className={styles.ctaActions}>
                <a
                  href={content.primary_link.href}
                  className="btn btn-rounded btn-dark-gray btn-medium btn-switch-text btn-box-shadow text-transform-none"
                >
                  <span>
                    <span className="btn-double-text" data-text={content.primary_link.label}>
                      {content.primary_link.label}
                    </span>
                    <span>
                      <i className="fa-solid fa-arrow-right"></i>
                    </span>
                  </span>
                </a>

                <a className={styles.secondaryLink} href={content.secondary_link.href}>
                  {content.secondary_link.label}
                </a>
              </div>
            </div>
          </div>
        </section>
      </article>
    </InnerPageChrome>
  );
}
